import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';
import { decorateEventsWithDiscovery } from '../utils/eventDiscovery';

export function useEvents(currentUserId, blockedIds = [], { includeCancelled = false } = {}) {
  const queryClient = useQueryClient();

  // Realtime: 報名狀態會改變近期熱門，推薦設定會改變策展區塊。
  useEffect(() => {

    const channel = supabase
      .channel('realtime-event-discovery')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'event_participants'
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['events'] });
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'event_recommendations'
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['events'] });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [currentUserId, queryClient]);

  return useQuery({
    queryKey: ['events', currentUserId, includeCancelled],
    queryFn: async () => {
      const [eventsResult, friendshipsResult, favoritesResult, recommendationsResult, trendingResult] = await Promise.all([
        supabase
          .from('events')
          .select('*, organizer:profiles!organizer_id(*), event_participants(user_id, status, profiles(id, name, avatar)), event_comments(id, user_id, content, created_at, profiles(name, avatar))')
          .order('date', { ascending: true }),
        currentUserId ? supabase
          .from('friendships')
          .select('user_id, friend_id')
          .or(`user_id.eq.${currentUserId},friend_id.eq.${currentUserId}`)
          .eq('status', 'accepted') : Promise.resolve({ data: [] }),
        currentUserId ? supabase
          .from('favorite_events')
          .select('event_id')
          .eq('user_id', currentUserId) : Promise.resolve({ data: [] }),
        supabase
          .from('event_recommendations')
          .select('event_id, recommendation_type, starts_at, ends_at, priority')
          .order('priority', { ascending: false }),
        supabase
          .from('event_recent_trending_scores')
          .select('event_id, recent_approved_count, trending_score')
      ]);

      if (eventsResult.error) throw eventsResult.error;

      const discoveryErrors = [recommendationsResult.error, trendingResult.error].filter(Boolean);
      if (discoveryErrors.length > 0) {
        console.error('載入推薦與近期熱門資料失敗:', discoveryErrors);
      }

      const friendIdSet = new Set();
      (friendshipsResult.data || []).forEach(f => {
        friendIdSet.add(f.user_id);
        friendIdSet.add(f.friend_id);
      });
      friendIdSet.delete(currentUserId);

      const userFavorites = (favoritesResult.data || []).map(f => f.event_id.toString());

      const normalizedEvents = (eventsResult.data || [])
        .filter(event => !blockedIds.includes(event.organizer_id) && (includeCancelled || event.status !== 'cancelled'))
        .map(event => ({
          ...event,
          _id: event.id,
          startTime: event.start_time || '',
          endTime: event.end_time || '',
          organizer: event.organizer ? {
            _id: event.organizer.id,
            id: event.organizer.id,
            profile: event.organizer
          } : null,
          participants: (event.event_participants || [])
            .filter(ep => ep.status === 'approved')
            .map(ep => ep.user_id),
          participantStatuses: Object.fromEntries(
            (event.event_participants || []).map(ep => [ep.user_id, ep.status])
          ),
          participantDetails: (event.event_participants || [])
            .filter(ep => ep.status === 'approved')
            .map(ep => ep.profiles)
            .filter(Boolean)
            .map(p => ({ _id: p.id, id: p.id, profile: p })),
          commentsCount: (event.event_comments || []).length,
          comments: event.event_comments || []
        }));

      const events = decorateEventsWithDiscovery(
        normalizedEvents,
        recommendationsResult.data || [],
        trendingResult.data || []
      );

      return {
        events,
        userFavorites,
        userFriends: Array.from(friendIdSet),
        discoveryError: discoveryErrors.length > 0
          ? '推薦與熱門資訊暫時無法更新，活動仍可正常瀏覽。'
          : null
      };
    },
    staleTime: 30 * 1000, // 降低 staleTime，讓資料更快刷新
  });
}
